<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PreChatFields from './PreChatFields.vue';
import { getPreChatFields, standardFieldKeys } from 'dashboard/helper/preChat';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PreChatFields,
    WootMessageEditor,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      preChatFormEnabled: false,
      preChatMessage: '',
      preChatFields: [],
      teamRoutingEnabled: false,
      teamRoutingPlaceholder: '',
      teamRoutingOptions: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      customAttributes: 'attributes/getAttributes',
      teams: 'teams/getTeams',
    }),
    preChatFieldOptions() {
      const { pre_chat_form_options: preChatFormOptions } = this.inbox;
      return getPreChatFields({
        preChatFormOptions,
        customAttributes: this.customAttributes,
      });
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
    this.$store.dispatch('teams/get');
  },
  methods: {
    setDefaults() {
      const { pre_chat_form_enabled: preChatFormEnabled } = this.inbox;
      this.preChatFormEnabled = preChatFormEnabled;
      const {
        pre_chat_message: preChatMessage,
        pre_chat_fields: preChatFields,
        team_routing_enabled: teamRoutingEnabled,
        team_routing_placeholder: teamRoutingPlaceholder,
        team_routing_options: teamRoutingOptions,
      } = this.preChatFieldOptions || {};
      this.preChatMessage = preChatMessage;
      this.preChatFields = preChatFields;
      this.teamRoutingEnabled = teamRoutingEnabled || false;
      this.teamRoutingPlaceholder =
        teamRoutingPlaceholder ||
        this.$t(
          'INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.PLACEHOLDER.PLACEHOLDER'
        );
      this.teamRoutingOptions = teamRoutingOptions || [];
    },
    isFieldEditable(item) {
      return !!standardFieldKeys[item.name] || !item.enabled;
    },
    isTeamSelected(teamId) {
      return this.teamRoutingOptions.some(option => option.team_id === teamId);
    },
    getTeamOption(teamId) {
      return this.teamRoutingOptions.find(option => option.team_id === teamId);
    },
    getTeamLabel(teamId) {
      const option = this.getTeamOption(teamId);
      return option?.custom_label || '';
    },
    isTeamDefault(teamId) {
      const option = this.getTeamOption(teamId);
      return option?.is_default || false;
    },
    toggleTeam(teamId, teamName) {
      const index = this.teamRoutingOptions.findIndex(
        option => option.team_id === teamId
      );
      if (index > -1) {
        this.teamRoutingOptions.splice(index, 1);
      } else {
        this.teamRoutingOptions.push({
          team_id: teamId,
          custom_label: teamName,
          is_default: false,
        });
      }
    },
    updateTeamLabel(teamId, label) {
      const option = this.getTeamOption(teamId);
      if (option) {
        option.custom_label = label;
      }
    },
    setDefaultTeam(teamId) {
      this.teamRoutingOptions.forEach(option => {
        option.is_default = option.team_id === teamId;
      });
    },
    handlePreChatFieldOptions(event, type, item) {
      this.preChatFields.forEach((field, index) => {
        if (field.name === item.name) {
          this.preChatFields[index][type] = !item[type];
        }
      });
    },

    changePreChatFieldFieldsOrder(updatedPreChatFieldOptions) {
      this.preChatFields = updatedPreChatFieldOptions;
    },

    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            pre_chat_form_enabled: this.preChatFormEnabled,
            pre_chat_form_options: {
              pre_chat_message: this.preChatMessage,
              pre_chat_fields: this.preChatFields,
              team_routing_enabled: this.teamRoutingEnabled,
              team_routing_placeholder: this.teamRoutingPlaceholder,
              team_routing_options: this.teamRoutingOptions,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-8 my-2 text-base">
    <div class="mx-0 mt-6 mb-3">
      {{ $t('INBOX_MGMT.PRE_CHAT_FORM.DESCRIPTION') }}
    </div>
    <form class="flex flex-col" @submit.prevent="updateInbox">
      <label class="w-1/4">
        {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.LABEL') }}
        <select v-model="preChatFormEnabled">
          <option :value="true">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.ENABLED') }}
          </option>
          <option :value="false">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.DISABLED') }}
          </option>
        </select>
      </label>
      <div v-if="preChatFormEnabled">
        <div>
          <label>
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.LABEL') }}
          </label>
          <WootMessageEditor
            v-model="preChatMessage"
            class="message-editor"
            :placeholder="
              $t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.PLACEHOLDER')
            "
          />
        </div>

        <div class="mt-6">
          <label class="flex items-center gap-2">
            <input
              v-model="teamRoutingEnabled"
              type="checkbox"
              class="size-4"
            />
            <span class="font-medium">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.ENABLE.LABEL') }}
            </span>
          </label>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.ENABLE.HELP_TEXT') }}
          </p>
        </div>

        <div v-if="teamRoutingEnabled" class="mt-4 space-y-4">
          <div>
            <label class="block font-medium mb-2">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.TEAMS.LABEL') }}
            </label>
            <p class="mb-3 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.TEAMS.HELP_TEXT') }}
            </p>
            <div v-if="teams.length > 0" class="space-y-3">
              <div
                v-for="team in teams"
                :key="team.id"
                class="border border-n-slate-6 rounded p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    :checked="isTeamSelected(team.id)"
                    class="size-4"
                    @change="toggleTeam(team.id, team.name)"
                  />
                  <span class="font-medium">{{ team.name }}</span>
                </div>

                <div v-if="isTeamSelected(team.id)" class="ml-6 space-y-2">
                  <div>
                    <label class="block text-sm text-n-slate-11 mb-1">
                      {{
                        $t(
                          'INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.CUSTOM_LABEL.LABEL'
                        )
                      }}
                    </label>
                    <input
                      :value="getTeamLabel(team.id)"
                      type="text"
                      class="w-full text-sm"
                      :placeholder="team.name"
                      @input="updateTeamLabel(team.id, $event.target.value)"
                    />
                  </div>

                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="default_team"
                      :checked="isTeamDefault(team.id)"
                      class="size-4"
                      @change="setDefaultTeam(team.id)"
                    />
                    <span class="text-sm">
                      {{
                        $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.SET_DEFAULT')
                      }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <p v-else class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.TEAMS.EMPTY') }}
            </p>
          </div>

          <div>
            <label>
              {{
                $t('INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.PLACEHOLDER.LABEL')
              }}
            </label>
            <input
              v-model="teamRoutingPlaceholder"
              type="text"
              class="mt-1 w-full"
              :placeholder="
                $t(
                  'INBOX_MGMT.PRE_CHAT_FORM.TEAM_ROUTING.PLACEHOLDER.PLACEHOLDER'
                )
              "
            />
          </div>
        </div>

        <div class="mt-4">
          <label>{{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS') }}</label>
          <table class="table w-full table-striped mt-4">
            <thead class="thead-dark">
              <tr
                class="[&>th]:font-semibold [&>th]:tracking-[1px] ltr:[&>th]:text-left rtl:[&>th]:text-right [&>th]:px-2.5 [&>th]:uppercase [&>th]:text-n-slate-12"
              >
                <th scope="col" />
                <th scope="col" />
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.KEY') }}
                </th>
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.TYPE') }}
                </th>
                <th scope="col">
                  {{
                    $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.REQUIRED')
                  }}
                </th>
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.LABEL') }}
                </th>
                <th scope="col">
                  {{
                    $t(
                      'INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.PLACE_HOLDER'
                    )
                  }}
                </th>
              </tr>
            </thead>
            <PreChatFields
              :pre-chat-fields="preChatFields"
              @update="handlePreChatFieldOptions"
              @drag-end="changePreChatFieldFieldsOrder"
            />
          </table>
        </div>
      </div>
      <div class="w-auto my-4">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE_PRE_CHAT_FORM_SETTINGS')"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
.message-editor {
  @apply px-3;

  ::v-deep {
    .ProseMirror-menubar {
      @apply rounded-tl-[4px];
    }
  }
}
</style>
